import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { ApiProblem } from '../api/client';
import {
  acceptLegalDocuments,
  fetchLegalAcceptance,
  type LegalDocument,
} from '../api/legal';

// #401 — the staff half of the legal acceptance gate.
//
// A hard gate: nothing this component wraps renders until the API says every
// current document version has been accepted. A WRAPPER rather than a page the
// shell can navigate to, because the property that has to hold is "no app
// content reaches the DOM" — a wrapper makes that structural instead of
// something every future page has to remember.
//
// One honest limit on the claim above: it is about RENDERING, not about network
// traffic. This component is mounted INSIDE the provider tree, so the fetches
// those providers fire on mount — AppDataProvider's cases query, for one —
// are already in flight while the gate is deciding, and are not cancelled if
// it blocks. Nobody
// SEES their results before accepting, which is what the gate is for, but "no
// app content reaches the DOM" must not be read as "the app does nothing until
// you accept". Do not extend it that far.
//
// That is a deliberate scope, not an oversight. The gate is a control over what
// a person can see and do in the UI; it is not the authorization boundary. The
// server-side controls are unchanged and are what actually decide who may read
// what — RLS on every table, the role guards, the customer tier — and the API
// does not refuse those calls on acceptance grounds. Restructuring the
// providers to defer their fetches would buy no security and would mean every
// accepting user waited out a second round trip.
//
// Staff are gated as well as customers, by the 2026-08-20 decision recorded in
// the issue. An analyst reads the same acceptable-use rules they enforce.
//
// The customer portal has its own copy at
// apps/portal/src/pages/LegalAcceptanceGate.tsx. The two bundles deliberately
// share no code (apps/portal/vite.config.ts), the same reason lib/theme.ts is
// duplicated between them.

type Phase =
  // Checking. Renders nothing at all rather than a spinner over app content:
  // the point is that nothing gets through before the answer arrives.
  | { kind: 'checking' }
  | { kind: 'blocked'; documents: LegalDocument[] }
  | { kind: 'open' }
  // The check itself failed. Fail CLOSED — see the comment in `load`.
  | { kind: 'unavailable'; message: string };

function problemMessage(err: unknown): string {
  if (err instanceof ApiProblem && err.detail) return err.detail;
  return (err as Error).message;
}

export function LegalAcceptanceGate({ children }: { children: ReactNode }): JSX.Element {
  const [phase, setPhase] = useState<Phase>({ kind: 'checking' });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const status = await fetchLegalAcceptance();
      setPhase(status.pending.length === 0
        ? { kind: 'open' }
        : { kind: 'blocked', documents: status.pending });
    } catch (err) {
      // 401/403 means this token cannot reach the staff API at all — no bcid-
      // realm role, which the RLS hook refuses. Such a session cannot reach any
      // content either, so letting it through costs nothing and leaves the
      // shell's own error handling to say what is wrong. (#419 sends a
      // customer-only login to /apply/ before this component is constructed, so
      // that account never reaches this gate at all.)
      if (err instanceof ApiProblem && (err.status === 401 || err.status === 403)) {
        setPhase({ kind: 'open' });
        return;
      }
      // Everything else fails CLOSED. An acceptance check that could not be
      // performed is not one that passed, and a compliance gate that quietly
      // disappears when a dependency is down is worse than one that says so.
      setPhase({ kind: 'unavailable', message: problemMessage(err) });
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (phase.kind === 'open') return <>{children}</>;
  if (phase.kind === 'checking') return <div style={styles.app} />;

  const accept = async (documents: LegalDocument[]) => {
    setSubmitting(true);
    setError(null);
    try {
      const status = await acceptLegalDocuments(documents);
      if (status.pending.length === 0) {
        setPhase({ kind: 'open' });
        return;
      }
      // A bump landed between the read and the click: show the new set rather
      // than letting anyone through on the strength of the old one.
      setPhase({ kind: 'blocked', documents: status.pending });
      setAgreed(false);
      setError('The documents were updated while this page was open. Please review the current versions.');
    } catch (err) {
      if (err instanceof ApiProblem && err.status === 409) {
        await load();
        setAgreed(false);
        setError('The documents were updated while this page was open. Please review the current versions.');
      } else {
        setError(problemMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ ...styles.app, display: 'flex', justifyContent: 'center', padding: 24 }}>
      <div style={{ ...styles.card, maxWidth: 640, width: '100%', alignSelf: 'flex-start', marginTop: 40 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: C.text, margin: '0 0 8px' }}>
          Before you continue
        </h1>

        {phase.kind === 'unavailable' ? (
          <>
            <p style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>
              We could not check which agreements apply to your account, so the portal is
              unavailable for now. This is a problem on the server, not with your sign-in.
            </p>
            <div
              role="alert"
              style={{ ...styles.card, borderLeft: `3px solid ${C.danger}`, color: C.danger, marginBottom: 16 }}
            >{phase.message}</div>
            <button type="button" style={{ ...styles.btn, ...styles.btnGold }} onClick={() => { void load(); }}>
              Try again
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>
              382 Communications has published {phase.documents.length === 1 ? 'a document' : 'documents'} you
              need to accept before using the BCID portal. Please read
              {phase.documents.length === 1 ? ' it' : ' them'} and confirm below.
            </p>

            <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0' }}>
              {phase.documents.map((d) => (
                <li key={d.slug} style={{ padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                  {/* Opens in a new tab so reading a document does not throw
                      away this screen — and these are plain anchors to pages
                      nginx serves, not setPage routes in this bundle. */}
                  <a href={d.path} target="_blank" rel="noopener noreferrer" style={styles.legalLink}>
                    {d.title}
                  </a>
                  {/* The version is shown, not just recorded: it is what the
                      acceptance is filed against, and it is how someone can
                      tell a re-gate from a bug. */}
                  <span style={{ fontSize: 11, color: C.textMuted, marginLeft: 8 }}>version {d.version}</span>
                </li>
              ))}
            </ul>

            {error && (
              <div
                role="alert"
                style={{ ...styles.card, borderLeft: `3px solid ${C.danger}`, color: C.danger, marginBottom: 16 }}
              >{error}</div>
            )}

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: C.text }}>
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
              <span>
                I have read and agree to the {phase.documents.length === 1 ? 'document' : 'documents'} listed above.
              </span>
            </label>

            <div style={{ marginTop: 20 }}>
              {/* Disabled until the box is ticked: the click IS the record, so
                  it must not be possible to make it by accident. */}
              <button
                type="button"
                style={{
                  ...styles.btn,
                  ...styles.btnGold,
                  ...(agreed && !submitting ? {} : { opacity: 0.5, cursor: 'not-allowed' }),
                }}
                disabled={!agreed || submitting}
                onClick={() => { void accept(phase.documents); }}
              >{submitting ? 'Saving…' : 'Accept and continue'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
